import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FcBusinessman, FcDepartment, FcHome, FcLibrary } from "react-icons/fc";
import { BsCurrencyEuro } from "react-icons/bs";
import { HiArrowRight, HiOutlineGlobeAlt } from "react-icons/hi2";
import useInput from "../../hooks/userInput";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";

import cf from "../UI/ClassicForm.module.css";
import Spinner from "../UI/Spinner";
import { transferActions } from "../../store/transfer-slice";
import { alertActions } from "../../store/alert-slice";
import { sendToken } from "../../api/api";

const International = () => {
  const [showSpinner, setShowSpinner] = useState(false);
  const [amount, setAmount] = useState("0.00");
  const dispatch = useDispatch();
  const { jwt } = useCookies(["jwt"])[0];
  const sender = useSelector((state) => state.auth.user.accountNumber);
  const navigate = useNavigate();
  const amountRef = useRef(null);
  const {
    value: bankNameInput,
    enteredValueIsValid: bankNameInputIsValid,
    hasError: bankNameInputIsInvalid,
    valueInputChangedHandler: bankNameInputChangedHandler,
    valueInputBlurHandler: bankNameInputBlurHandler,
  } = useInput((value) => value.trim() !== "");

  const {
    value: nameInput,
    enteredValueIsValid: nameInputIsValid,
    hasError: nameInputIsInvalid,
    valueInputChangedHandler: nameInputChangedHandler,
    valueInputBlurHandler: nameInputBlurHandler,
  } = useInput((value) => value.trim() !== "");

  const {
    value: IBANInput,
    enteredValueIsValid: IBANInputIsValid,
    hasError: IBANInputIsInvalid,
    valueInputChangedHandler: IBANInputChangedHandler,
    valueInputBlurHandler: IBANInputBlurHandler,
  } = useInput((value) => value.trim() !== "");

  const {
    value: swiftInput,
    enteredValueIsValid: swiftInputIsValid,
    hasError: swiftInputIsInvalid,
    valueInputChangedHandler: swiftInputChangedHandler,
    valueInputBlurHandler: swiftInputBlurHandler,
  } = useInput((value) => value.trim() !== "");

  const handleAmountChange = (e) => {
    const enteredValue = e.target.value.replace(/[^\d]/g, "");

    const decimalPart = enteredValue.slice(-2);
    const integerPart = enteredValue.slice(0, -2).replace(/^0+/, "");

    const formattedIntegerPart = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      ","
    );

    const formattedValue = `${
      formattedIntegerPart.length === 0 ? "0" : formattedIntegerPart
    }.${decimalPart}`;

    setAmount(formattedValue);

    if (amountRef.current) {
      amountRef.current.setSelectionRange(
        formattedValue.length,
        formattedValue.length
      );
    }
  };

  let formIsValid = false;
  if (
    bankNameInputIsValid &&
    nameInputIsValid &&
    IBANInputIsValid &&
    swiftInputIsValid &&
    amount !== "0.00"
  ) {
    formIsValid = true;
  }

  const submitHandler = async (e) => {
    e.preventDefault();
    setShowSpinner(true);

    const transactionInfo = {
      kind: "International",
      type: "debit",
      bankName: bankNameInput,
      receipientName: nameInput,
      receipient: IBANInput,
      amount: amount.replace(/,/g, ""),
      sender,
      swift: swiftInput,
    };

    const res = await sendToken(jwt);

    if (res.status === "success") {
      dispatch(transferActions.transfer({ transactions: transactionInfo }));
      navigate("/account/confirmInternational");
    } else {
      dispatch(
        alertActions.setState({ message: res.message, status: "error" })
      );
    }

    setShowSpinner(false);
  };

  const nameInputClasses = nameInputIsInvalid
    ? `${cf.field} ${cf.invalid}`
    : cf.field;
  const bankNameInputClasses = bankNameInputIsInvalid
    ? `${cf.field} ${cf.invalid}`
    : cf.field;
  const IBANInputClasses = IBANInputIsInvalid
    ? `${cf.field} ${cf.invalid}`
    : cf.field;
  const swiftInputClasses = swiftInputIsInvalid
    ? `${cf.field} ${cf.invalid}`
    : cf.field;

  return (
    <form className={cf.form} onSubmit={submitHandler}>
      {showSpinner && <Spinner />}
      <div className={cf.formTitleRow}>
        <HiOutlineGlobeAlt className={cf.formTitleIcon} aria-hidden />
        <h2 className={cf.formTitle}>International transfer</h2>
      </div>
      <p className={cf.formSubtitle}>
        Provide bank and beneficiary details in full. We will email a
        confirmation token next.
      </p>
      <div className={bankNameInputClasses}>
        <label htmlFor="int-bank">Bank name</label>
        <div className={cf.inputGroup}>
          <FcHome className={cf.icon} />
          <input
            id="int-bank"
            type="text"
            value={bankNameInput}
            onChange={bankNameInputChangedHandler}
            onBlur={bankNameInputBlurHandler}
          />
        </div>
        {bankNameInputIsInvalid && (
          <span className={cf.fieldError}>Please enter beneficiary bank name</span>
        )}
      </div>
      <div className={nameInputClasses}>
        <label htmlFor="int-name">Beneficiary name</label>
        <div className={cf.inputGroup}>
          <FcBusinessman className={cf.icon} />
          <input
            id="int-name"
            type="text"
            value={nameInput}
            onChange={nameInputChangedHandler}
            onBlur={nameInputBlurHandler}
          />
        </div>
        {nameInputIsInvalid && (
          <span className={cf.fieldError}>Please enter beneficiary name</span>
        )}
      </div>
      <div className={IBANInputClasses}>
        <label htmlFor="int-iban">IBAN</label>
        <div className={cf.inputGroup}>
          <FcDepartment className={cf.icon} />
          <input
            id="int-iban"
            type="text"
            value={IBANInput}
            onChange={IBANInputChangedHandler}
            onBlur={IBANInputBlurHandler}
          />
        </div>
        {IBANInputIsInvalid && (
          <span className={cf.fieldError}>Please enter valid IBAN</span>
        )}
      </div>
      <div className={swiftInputClasses}>
        <label htmlFor="int-swift">Swift / sort code</label>
        <div className={cf.inputGroup}>
          <FcLibrary className={cf.icon} />
          <input
            id="int-swift"
            type="text"
            value={swiftInput}
            onChange={swiftInputChangedHandler}
            onBlur={swiftInputBlurHandler}
          />
        </div>
        {swiftInputIsInvalid && (
          <span className={cf.fieldError}>Please enter swift/sortcode</span>
        )}
      </div>
      <div className={cf.field}>
        <label htmlFor="int-amount">Amount</label>
        <div className={cf.inputGroup}>
          <BsCurrencyEuro className={cf.icon} />
          <input
            id="int-amount"
            type="text"
            value={amount}
            onChange={handleAmountChange}
            ref={amountRef}
          />
        </div>
      </div>
      <div className={cf.actions}>
        <button type="submit" disabled={!formIsValid}>
          <HiArrowRight className={cf.btnIcon} aria-hidden />
          Next
        </button>
      </div>
    </form>
  );
};

export default International;
